import {Box, SxProps, Theme} from '@mui/material';
import {usePersona} from 'src/permissions/hooks';
import {FC, useEffect} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate, useParams} from 'react-router-dom';
import {locationsApi} from 'src/api/locations';
import {useAppSelector} from 'src/app/hooks';
import {store} from 'src/app/store';
import {PowerConsumption} from 'src/components/common/PowerConsumption';
import {ClientListInLocationDetails} from 'src/components/locations/ClientListInLocationDetails';
import {LocationChargersList} from 'src/components/locations/LocationChargersList';
import {LocationDetails} from 'src/components/locations/LocationDetails';
import {LocationFiles} from 'src/components/locations/LocationFiles';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {GetLocationByIdType} from 'src/types/locations';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {LocationInfoProvider} from './LocationInfo.context';

type LocationQueryTrigger = ReturnType<typeof locationsApi.useLazyGetLocationByIdQuery>[0];

const handleGetLocation =
    (trigger: LocationQueryTrigger) => async (data: GetLocationByIdType) => {
        store.dispatch(preloaderChangeStatus(true));

        try {
            const response = await trigger(data).unwrap();
            store.dispatch(preloaderChangeStatus(false));

            return response;
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            store.dispatch(preloaderChangeStatus(false));
        }
    };

const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

/** Clients and files keep the eight-to-four split they had, as two cards in a row. */
const listsRowSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', lg: '8fr 4fr'},
    gap: 2,
    alignItems: 'start',
};

export const LocationInfo: FC = () => {
    const params = useParams();
    const persona = usePersona();
    const intl = useIntl();
    const {activeBusiness, authInfo} = useAppSelector((state) => state.common);
    const isCompanyUser = persona.isCompany;
    const isEmployeeUser = persona.isEmployee;
    const isDriverUser = persona.isDriver;

    const [trigger, response] = locationsApi.useLazyGetLocationByIdQuery();

    // `singlePages` was one subject standing in for every detail page, so a role that could read
    // locations but not tariffs got the same answer for both. Each page now asks about its own thing.
    // The client list, files and charger list are the back-office view of a location. No endpoint
    // draws that line, so there is no permission to name and inventing one would put a right in the
    // catalog that nothing enforces. Persona reproduces the old rule exactly: hidden from drivers,
    // employees and companys, shown to the business and its staff.
    const canReadLocationDetails = persona.isBackOffice;
    const navigate = useNavigate();

    useEffect(() => {
        // Only the malformed-URL half. Whether this page may be opened at all is PermissionRoute's
        // question, and it asks locations.read - the same permission this used to re-check.
        if (!params.id) {
            return navigate(`/404`);
        }
        const data: GetLocationByIdType = {
            locationId: +params.id,
            businessId: activeBusiness,
        };
        // No realEstateId or companyId from the account: the server forces whichever of them
        // matches the caller's reach onto the request, and for a caller whose reach is wider
        // they were never theirs to choose.
        void handleGetLocation(trigger)(data);
    }, []);
    return (
        <Box sx={pageSx}>
            <LocationInfoProvider value={{businessId: 1}}>
                {response.data && (
                    <>
                        {/* LocationDetails renders two cards now — the place and its pricing — so the
                            page stacks them rather than wrapping each in a grid cell. */}
                        <LocationDetails
                            data={response.data}
                            canReadDetails={canReadLocationDetails}
                        />

                        <PowerConsumption
                            locationId={response.data.id}
                            companyId={isCompanyUser ? authInfo?.company?.id : undefined}
                            userId={(isDriverUser || isEmployeeUser) ? authInfo?.id : undefined}
                        />

                        {canReadLocationDetails && (
                            <>
                                <Box sx={listsRowSx}>
                                    {params.id && (
                                        <ClientListInLocationDetails
                                            title={intl.formatMessage({id: 'clientsList'})}
                                            users={response.data.users}
                                            companies={response.data.companies}
                                            locationId={+params.id}
                                            businessId={response.data.business.id}
                                        />
                                    )}
                                    <LocationFiles
                                        title={intl.formatMessage({id: 'files'})}
                                        data={response.data}
                                    />
                                </Box>

                                <LocationChargersList
                                    title={intl.formatMessage({id: 'chargers'})}
                                    locationId={response.data.id}
                                    businessId={response.data.business.id}
                                />
                            </>
                        )}
                    </>
                )}
            </LocationInfoProvider>
        </Box>
    );
};
